import { useEffect, useRef, useState } from 'react'
import { Badge, Button, Group, Stack, Text, Textarea, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'

import type { ConversaWhatsApp } from '@/api/whatsapiOrg'
import { useAssumirConversa, useResponderConversa } from '@/hooks/useConversasWhatsApi'
import { useDevolverConversa } from '@/hooks/useWhatsappGateways'
import { ROTULO_DO_ESTADO, voltaAoBotEm } from './textos'

const TEXTO_MAX = 4096

/**
 * Coluna do painel (spec 2026-11-02 conversas §5.7): o estado, "volta ao bot em mm:ss",
 * Assumir, Devolver ao bot e a caixa de resposta — esta só para quem assumiu. Sem
 * `whatsapi:atender` a conversa é só leitura. `conversa` nulo: a conversa aberta não está na
 * página carregada da lista (veio de um link): sem saber o estado, nenhuma ação aparece.
 */
export function PainelDaConversa({
  orgId, gwId, conversaRef, conversa, podeAtender,
}: {
  orgId: number
  gwId: string
  conversaRef: string
  conversa: ConversaWhatsApp | null
  podeAtender: boolean
}) {
  const assumir = useAssumirConversa(orgId, gwId)
  const responder = useResponderConversa(orgId, gwId)
  const devolver = useDevolverConversa(orgId, gwId)
  const [texto, setTexto] = useState('')
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1_000)
    return () => clearInterval(t)
  }, [])

  const estado = conversa?.estado
  const minha = estado === 'em_atendimento' && !!conversa?.atendente_e_voce
  const deOutro = estado === 'em_atendimento' && !conversa?.atendente_e_voce
  // Uma resposta por vez: dois Ctrl+Enter no mesmo instante mandariam duas mensagens no WhatsApp.
  // A ref fecha a janela antes de `isPending` chegar à próxima renderização.
  const enviando = useRef(false)
  const enviar = () => {
    if (!texto.trim() || responder.isPending || enviando.current) return
    enviando.current = true
    responder.mutate({ ref: conversaRef, texto }, {
      onSettled: () => { enviando.current = false },
      onSuccess: (r) => {
        // 201 com `failed`: o WhatsApi gravou a resposta, mas o gateway não a entregou. O
        // texto fica na caixa para tentar de novo; o balão aparece como "não entregue".
        if (r.status === 'failed') {
          notifications.show({ color: 'red', title: 'Resposta não entregue',
            message: 'O gateway não conseguiu enviar pelo WhatsApp. Confira o gateway e tente de novo.' })
          return
        }
        setTexto('')
      },
    })
  }

  return (
    <Stack gap="sm">
      <Title order={5}>{conversa?.numero ?? 'Conversa'}</Title>
      {estado ? (
        <Group gap="xs">
          <Badge variant="light" color={estado === 'aguardando' ? 'orange' : estado === 'em_atendimento' ? 'green' : 'blue'}>
            {ROTULO_DO_ESTADO[estado]}
          </Badge>
          {conversa && conversa.gateway_nome && <Text size="xs" c="dimmed">{conversa.gateway_nome}</Text>}
        </Group>
      ) : (
        <Text size="sm" c="dimmed">Estado ainda não carregado.</Text>
      )}
      {estado === 'em_atendimento' && (
        <Text size="sm">{minha ? 'Você está atendendo.' : `${conversa?.atendente_nome ?? 'Outra pessoa'} está atendendo.`}</Text>
      )}
      {estado && estado !== 'bot' && conversa?.pausado_ate && (
        <Text size="sm" c="dimmed">Volta ao bot em {voltaAoBotEm(conversa.pausado_ate, agora)}</Text>
      )}

      {!podeAtender ? (
        <Text size="sm" c="dimmed">Você pode ler esta conversa, mas não atender nesta organização.</Text>
      ) : !estado ? null : (
        <>
          <Group gap="xs">
            {!minha && (
              <Button size="xs" loading={assumir.isPending} disabled={deOutro}
                onClick={() => assumir.mutate(conversaRef)}>
                Assumir
              </Button>
            )}
            {estado !== 'bot' && (
              <Button size="xs" variant="light" loading={devolver.isPending}
                onClick={() => devolver.mutate(conversaRef)}>
                Devolver ao bot
              </Button>
            )}
          </Group>
          {deOutro && (
            <Text size="xs" c="dimmed">
              Só quem assumiu responde. A conversa fica livre quando {conversa?.atendente_nome ?? 'a outra pessoa'} devolver ao bot ou a pausa vencer.
            </Text>
          )}
          {minha && (
            <Stack gap="xs">
              <Textarea aria-label="Resposta" placeholder="Escreva a resposta" autosize minRows={3} maxRows={8}
                maxLength={TEXTO_MAX} value={texto} onChange={(e) => setTexto(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar() }} />
              <Group justify="space-between">
                <Text size="xs" c="dimmed">{texto.length}/{TEXTO_MAX} · Ctrl+Enter envia</Text>
                <Button size="xs" loading={responder.isPending} disabled={!texto.trim()} onClick={enviar}>
                  Enviar
                </Button>
              </Group>
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}
